import { motion } from "framer-motion";
import { Gift, Star, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { BUNDLE } from "@/lib/bundle";

const formatPrice = (p) => `€${p.toFixed(2).replace(".", ",")}`;

const ProductCard = ({ product, big = false, index }) => {
  const { addItem, setIsOpen } = useCart();

  const handleAdd = () => {
    addItem(product);
    toast.success(`${product.name} toegevoegd aan je winkelmand`, {
      action: { label: "Bekijk", onClick: () => setIsOpen(true) },
    });
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`group relative bg-slate-900/80 backdrop-blur-xl border border-slate-800 hover:border-[#00FF66]/50 rounded-3xl overflow-hidden transition-colors duration-300 ${big ? "md:col-span-2" : ""}`}
    >
      <div className={`spotlight-frame relative ${big ? "h-72 sm:h-96" : "h-64"} overflow-hidden`}>
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
        <span
          data-testid={`product-badge-${product.id}`}
          className="absolute top-4 left-4 bg-[#090D16]/80 backdrop-blur-md text-[#00FF66] border border-[#00FF66]/40 px-3 py-1 rounded-full text-xs font-mono-brand uppercase tracking-widest"
        >
          {product.badge}
        </span>
        <span className="absolute top-4 right-4 flex items-center gap-1 bg-[#090D16]/80 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-white">
          <Star className="w-3.5 h-3.5 text-[#FF087B] fill-[#FF087B]" />
          {product.rating.toFixed(1)}
        </span>
      </div>

      <div className="p-6">
        <h3 className="font-display text-3xl tracking-wide text-white uppercase" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        <p className="mt-1.5 font-mono-brand text-[11px] uppercase tracking-widest text-[#00E5FF]">{product.specs}</p>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{product.description}</p>

        <div className="mt-5 flex items-center justify-between">
          <span className="text-2xl font-extrabold text-white" data-testid={`product-price-${product.id}`}>
            {formatPrice(product.price)}
          </span>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            className="flex items-center gap-2 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-sm uppercase tracking-wide px-5 py-2.5 rounded-full hover:scale-105 active:scale-95 transition-transform duration-200 glow-lime"
          >
            <Plus className="w-4 h-4" strokeWidth={3} />
            In winkelmand
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export const ProductGrid = ({ products }) => {
  return (
    <section id="blasters" data-testid="products-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="font-mono-brand text-xs uppercase tracking-widest text-[#00FF66] mb-3">De collectie</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-wide text-white leading-none">
            Kies je <span className="text-gradient-splat">wapen</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">
            Vier modellen. Eén missie: maximale splat. Elke blaster wordt geleverd met 10.000 gel orbs, oplader en veiligheidsbril.
          </p>
          <div
            data-testid="bundle-banner"
            className="mt-6 inline-flex flex-wrap items-center gap-x-4 gap-y-2 bg-slate-900/70 border border-[#00FF66]/30 rounded-full px-5 py-2.5 text-sm"
          >
            <span className="flex items-center gap-2 font-bold text-[#00FF66]">
              <Gift className="w-4 h-4" /> Bundelkorting
            </span>
            <span className="text-slate-300">
              Vanaf {BUNDLE.minQty} blasters <b className="text-white">€{BUNDLE.discountPerBlaster} korting per blaster</b>
            </span>
            <span className="hidden sm:inline text-slate-600">•</span>
            <span className="text-slate-300">
              Bij {BUNDLE.giftQty} blasters <b className="text-[#00E5FF]">gratis {BUNDLE.giftName}</b>
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} big={i === 0 || i === 3} />
          ))}
        </div>
      </div>
    </section>
  );
};
